import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, STATUS_META } from "@/lib/helpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, AlertTriangle, ShieldAlert, Wrench, Truck, ChevronRight, ClipboardCheck, PenLine, GraduationCap, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";

const CAT_STYLE = {
  DNI: "text-sky-700 bg-sky-50",
  "Reconocimiento Médico": "text-purple-700 bg-purple-50",
  "Curso PRL": "text-indigo-700 bg-indigo-50",
  EPI: "text-orange-700 bg-orange-50",
  ITV: "text-teal-700 bg-teal-50",
};

function Stat({ icon: Icon, label, value, tone = "default", onClick }) {
  const tones = {
    default: "text-slate-900",
    red: "text-red-600",
    amber: "text-amber-600",
    accent: "text-accent",
  };
  return (
    <Card onClick={onClick}
      className={`p-5 flex items-center justify-between transition-all ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : ""}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`font-display text-3xl font-extrabold mt-1 ${tones[tone]}`}>{value}</p>
      </div>
      <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className={`h-5 w-5 ${tones[tone]}`} />
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [emps, setEmps] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [certs, setCerts] = useState([]);
  const [branding, setBranding] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/employees").then((r) => setEmps(r.data)).catch(() => setEmps([]));
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => setAlerts({ alerts: [], counts: {} }));
    api.get("/certificates/recent?limit=10").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  if (!emps || !alerts) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  const toolsCount = emps.reduce((a, e) => a + (e.tools?.length || 0), 0);
  const vehiclesCount = emps.reduce((a, e) => a + (e.vehicles?.length || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Panel Principal</h1>
        <p className="text-muted-foreground text-sm mt-1">Resumen del estado de tu plantilla y cumplimiento.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Empleados" value={emps.length} onClick={() => navigate("/empleados")} />
        <Stat icon={ShieldAlert} label="Caducados" value={alerts.counts.caducado || 0} tone="red"
          onClick={() => navigate("/caducidades")} />
        <Stat icon={AlertTriangle} label="Próximos a caducar" value={alerts.counts.proximo || 0} tone="amber"
          onClick={() => navigate("/caducidades")} />
        <Stat icon={Wrench} label="Herramientas prestadas" value={toolsCount} onClick={() => navigate("/herramientas")} />
      </div>

      {alerts.pending_signatures?.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="dashboard-pending-signatures">
          <PenLine className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <p className="font-semibold">{alerts.counts.sin_firma} EPI(s) entregados pendientes de firma del trabajador</p>
            <p className="text-xs">{alerts.pending_signatures.slice(0, 4).map((p) => `${p.employee_name} (${p.count})`).join(" · ")}{alerts.pending_signatures.length > 4 ? " · …" : ""}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/caducidades")} data-testid="dashboard-pending-signatures-button">Ver detalle</Button>
        </Card>
      )}

      {alerts.ending_trainings?.length > 0 && (
        <Card className="p-4 border-sky-200 bg-sky-50 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="dashboard-ending-trainings">
          <GraduationCap className="h-5 w-5 text-sky-600 shrink-0" />
          <div className="flex-1 text-sm text-sky-900">
            <p className="font-semibold">{alerts.counts.fin_formacion} alumno(s) terminan su formación en los próximos 7 días</p>
            <p className="text-xs">{alerts.ending_trainings.slice(0, 4).map((s) => `${s.name} (${s.days < 0 ? `hace ${-s.days} d` : s.days === 0 ? "hoy" : `en ${s.days} d`})`).join(" · ")}{alerts.ending_trainings.length > 4 ? " · …" : ""}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/alumnos")} data-testid="dashboard-ending-trainings-button">Ver alumnos</Button>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <h2 className="font-display text-lg font-bold">Avisos de caducidad</h2>
            </div>
            <button className="text-sm text-accent font-medium flex items-center gap-1 hover:underline"
              onClick={() => navigate("/caducidades")} data-testid="view-all-alerts-button">
              Ver todos <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="divide-y max-h-[420px] overflow-y-auto" data-testid="dashboard-alerts-list">
            {alerts.alerts.length === 0 && (
              <div className="p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
                <ClipboardCheck className="h-8 w-8 text-emerald-500" />
                Todo en regla. No hay caducidades próximas.
              </div>
            )}
            {alerts.alerts.slice(0, 12).map((a, i) => {
              const meta = STATUS_META[a.status];
              return (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${CAT_STYLE[a.category] || "bg-slate-100 text-slate-600"}`}>
                        {a.category}
                      </span>
                      <span className="text-sm font-medium truncate">{a.employee_name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {a.item} · vence {fmtDate(a.expiry)}
                    </p>
                  </div>
                  <Badge className={`${meta.cls} shrink-0`}>
                    {a.status === "caducado" ? `Caducado` : `${a.days} días`}
                  </Badge>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="font-display text-lg font-bold mb-4">Recursos asignados</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-slate-600"><Wrench className="h-4 w-4" /> Herramientas</span>
              <span className="font-mono font-semibold">{toolsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-slate-600"><Truck className="h-4 w-4" /> Vehículos</span>
              <span className="font-mono font-semibold">{vehiclesCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-slate-600"><Users className="h-4 w-4" /> Empleados activos</span>
              <span className="font-mono font-semibold">{emps.length}</span>
            </div>
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                Total de elementos con caducidad controlados:{" "}
                <span className="font-mono font-semibold text-slate-900">{alerts.counts.total_items || 0}</span>
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6" data-testid="dashboard-recent-certificates">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-accent" />
          <h2 className="font-display text-base md:text-lg font-bold tracking-tight">Últimos certificados emitidos</h2>
        </div>
        {certs.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Todavía no se ha emitido ningún certificado.</p>
        ) : (
          <ul className="mt-4 divide-y">
            {certs.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`recent-certificate-${c.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{c.employee_name}</p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{c.kind}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">{fmtDate(c.created_at?.slice(0, 10))}</span>
                  <button onClick={() => navigate("/empleados")} className="text-slate-400 transition-colors hover:text-accent"
                    aria-label="Ver empleado" data-testid={`goto-employee-${c.employee_id}`}>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {branding?.lopd_text && (
        <details className="rounded-lg border bg-white/60 p-4" data-testid="dashboard-lopd">
          <summary className="cursor-pointer text-xs font-medium uppercase tracking-wider text-slate-500 hover:text-slate-700">
            Protección de datos (LOPD / RGPD)
          </summary>
          <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{branding.lopd_text}</p>
        </details>
      )}
    </div>
  );
}
